import CustomModal from "../../../common/components/modal/CustomDetailModal";
import { Column, Divider, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C3Text, C4Text } from "../../../common/components/text/CustomText";
import React, { useEffect, useState } from "react";
import CustomInput from "../../../common/components/input/CustomInput";
import CustomIcon from "../../../common/components/image/CustomIcon";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import Translate from "../../../common/translate/Translate";
import { Theme } from "../../../common/Theme";
import StoreService from "../../../service/StoreService";

const PAGE_SIZE = 5;

// 04.01.03.34 기기 메모 조회 응답(result.memo[])을 화면에서 쓰는 형태로 변환.
const toMemoItem = (item) => ({
    id: item.memoId,
    memo: item.memo,
    createdAt: item.createDate,
});

function MemoCard({ memo, createdAt, onDelete, onEdit, isLast, isActive }) {
    return (
        <>
            <C4Text fontColor={Theme.color.gray500}>{createdAt}</C4Text>
            <SizedBox height={"6px"} />
            <SizedBox
                width={"100%"}
                backgroundColor={isActive ? "transparent" : Theme.color.neutral100}
                border={isActive ? `1px solid ${Theme.color.indigo500}` : "none"}
                height={"auto"}
                padding={"16px 12px 16px 16px"}
                alignItem={"flex-start"}
                justifyContent={"flex-start"}
                borderRadius={"12px"}
                onClick={onEdit}
                boxSizing={"border-box"}
            >
                <Row alignItem={"center"} justifyContent={"space-between"} width={"100%"}>
                    <C3Text fontColor={Theme.color.zinc850}>{memo}</C3Text>
                    <CustomIcon iconName={"close"} onClick={onDelete} size={"16px"} cursor={"pointer"} />
                </Row>
            </SizedBox>
            {!isLast && <SizedBox height={"16px"} />}
        </>
    );
}

function DeviceMemoModal({ isOpen, onClose, device }) {
    const storeService = new StoreService();
    const [memoContent, setMemoContent] = React.useState("");
    const [memo, setMemo] = useState([]);
    const [selectedMemo, setSelectedMemo] = useState(null);

    // 04.01.03.34는 메모 전체를 한 번에 반환하므로 전체 목록을 보관하고 페이지네이션은 클라이언트에서 처리.
    const [allMemo, setAllMemo] = useState([]);

    // pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);

    const applyPage = (list, page) => {
        const start = (page - 1) * PAGE_SIZE;
        setMemo(list.slice(start, start + PAGE_SIZE));
        setTotalPage(Math.max(1, Math.ceil(list.length / PAGE_SIZE)));
    };

    const fetchMemoList = () => {
        if (!device?.id) return;
        storeService
            .getDeviceMemo({ deviceId: device.id })
            .then((result) => {
                const list = (result?.memo ?? []).map(toMemoItem);
                setAllMemo(list);
                applyPage(list, 1);
            })
            .catch((error) => {
                console.error("Error fetching device memo:", error);
            });
    };

    useEffect(() => {
        if (isOpen) {
            setCurrentPage(1);
            fetchMemoList();
        }
    }, [isOpen]);

    const changePage = (page) => {
        setCurrentPage(page);
        applyPage(allMemo, page);
    };

    const resetInput = () => {
        setSelectedMemo(null);
        setMemoContent("");
    };

    // 선택된 메모가 없으면 등록(04.01.03.33), 있으면 수정(04.01.03.35).
    const handleSave = () => {
        if (!device?.id || !memoContent.trim()) return;
        const request =
            selectedMemo === null
                ? storeService.createDeviceMemo({ deviceId: device.id, memo: memoContent })
                : storeService.updateDeviceMemo({ deviceId: device.id, memoId: selectedMemo, memo: memoContent });
        request
            .then(() => {
                resetInput();
                fetchMemoList();
            })
            .catch((error) => {
                console.error("Error saving device memo:", error);
            });
    };

    // popup
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const openDeletePopup = (memoId) => {
        setDeleteTargetId(memoId);
        setIsPopupOpen(true);
    };

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setDeleteTargetId(null);
    };

    // 04.01.03.36 기기 메모 삭제
    const handlePopupOk = () => {
        if (deleteTargetId === null || !device?.id) {
            handlePopupClose();
            return;
        }
        storeService
            .deleteDeviceMemo({ deviceId: device.id, memoId: deleteTargetId })
            .then(() => {
                if (selectedMemo === deleteTargetId) {
                    resetInput();
                }
                fetchMemoList();
            })
            .catch((error) => {
                console.error("Error deleting device memo:", error);
            })
            .finally(() => {
                handlePopupClose();
            });
    };

    return (
        <CustomModal
            title={Translate("store.deviceMenu.memo")}
            isOpen={isOpen}
            onClose={onClose}
            side={"right"}
            width={"552px"}
            maxWidth={"552px"}
            height={"100%"}
            hasPagination={true}
            totalPage={totalPage}
            currentPage={currentPage}
            changePage={changePage}
            contentPadding={"20px 20px 0"}
        >
            <Row>
                <C3Text fontColor={Theme.color.gray500}>{Translate("store.deviceMenu.type")}</C3Text>
                <SizedBox width={"8px"} />
                <C3Text>{device.type}</C3Text>
                <Divider orientation={"vertical"} height={"16px"} marginLeft={"24px"} marginRight={"12px"} />
                <C3Text fontColor={Theme.color.gray500}>{Translate("store.deviceMenu.name")}</C3Text>
                <SizedBox width={"8px"} />
                <C3Text>{device.name}</C3Text>
                <Divider orientation={"vertical"} height={"16px"} marginLeft={"24px"} marginRight={"12px"} />
                <C3Text fontColor={Theme.color.gray500}>{Translate("store.deviceMenu.model")}</C3Text>
                <SizedBox width={"8px"} />
                <C3Text>{device.model}</C3Text>
            </Row>
            <Divider marginTop={"12px"} thickness={"0"} />
            <C3Text fontColor={Theme.color.gray500}>{Translate("store.deviceMenu.id")}</C3Text>
            <SizedBox width={"8px"} />
            <C3Text>{device.id}</C3Text>
            <SizedBox height={"20px"} width={"100%"} />
            <CustomInput
                type={"textAriaWithButton"}
                placeholder={"Memo"}
                inputValue={memoContent}
                setInputValue={setMemoContent}
                deleteButonText={selectedMemo === null ? Translate("common.reset") : Translate("common.delete")}
                onDelete={selectedMemo === null ? () => setMemoContent("") : () => openDeletePopup(selectedMemo)}
                onSave={handleSave}
            />
            <SizedBox height={"16px"} width={"100%"} />
            <SizedBox
                width={"100%"}
                height={"76%"}
                alignItem={"flex-start"}
                justifyContent={"flex-start"}
                borderRadius={"12px"}
                border={`1px solid ${Theme.color.gray200}`}
                overflow={"none"}
                padding={"16px 18px"}
            >
                <Column width={"100%"} alignItem={"flex-start"} justifyContent={"flex-start"}>
                    {memo.map((item, index) => (
                        <MemoCard
                            memo={item.memo}
                            createdAt={item.createdAt}
                            onDelete={() => openDeletePopup(item.id)}
                            onEdit={() => {
                                setSelectedMemo(item.id);
                                setMemoContent(item.memo);
                            }}
                            isLast={index === memo.length - 1}
                            key={item.id}
                            isActive={selectedMemo === item.id}
                        />
                    ))}
                </Column>
            </SizedBox>
            <CustomPopup
                isOpen={isPopupOpen}
                onClose={handlePopupClose}
                onConfirm={handlePopupOk}
                message={Translate("popup.deleteMemo")}
            />
        </CustomModal>
    );
}

export default DeviceMemoModal;
